import React, { useState } from "react";
import { Lock, CheckCircle2, ChevronDown, ChevronUp, Trophy } from "lucide-react";

/**
 * Level Income — 25-tier eligibility table.
 * Shows reward %, direct & self-deposit requirements per level,
 * with live lock/unlock state for the current user.
 */
export function LevelIncomeTable({ data }) {
  const [expanded, setExpanded] = useState(false);

  const levels = data?.levels || [];
  const unlocked = data?.unlocked_count || 0;
  const total = data?.total_levels || 25;
  const selfDep = data?.self_deposit || 0;
  const d50 = data?.directs_eff_50 || 0;
  const d100 = data?.directs_100 || 0;

  // Hide loading shell entirely if no data
  if (!data) {
    return (
      <div>
        <div className="text-[10px] uppercase tracking-[0.18em] text-[var(--text-3)] mb-2 px-1">Level Income</div>
        <div className="card-3d lift-on-hover p-6 text-center relative" data-testid="level-income-loading">
          <div className="text-xs text-[var(--text-3)]">Loading level status…</div>
        </div>
      </div>
    );
  }

  const visibleLevels = expanded ? levels : levels.slice(0, 5);

  return (
    <div data-testid="level-income-section">
      <div className="flex items-center justify-between mb-2 px-1">
        <div className="text-[10px] uppercase tracking-[0.18em] text-[var(--text-3)]">Level Income</div>
        <div className="text-[10px] mono text-[var(--text-3)]">
          <span style={{ color: "#FFD36A" }}>{unlocked}</span>
          <span className="mx-0.5 opacity-50">/</span>
          <span>{total}</span>
          <span className="ml-1 opacity-70">unlocked</span>
        </div>
      </div>

      <div
        className="card-3d lift-on-hover relative overflow-hidden p-0"
        style={{
          background:
            "linear-gradient(180deg, rgba(15,30,50,0.95) 0%, rgba(8,20,38,0.95) 100%)",
          border: "1px solid rgba(255,211,106,0.30)",
        }}
        data-testid="level-income-card"
      >
        {/* Gold side ornaments */}
        <div
          className="absolute inset-y-0 left-0 w-1.5 pointer-events-none"
          style={{
            background:
              "linear-gradient(180deg, transparent 0%, rgba(255,211,106,0.55) 50%, transparent 100%)",
          }}
        />
        <div
          className="absolute inset-y-0 right-0 w-1.5 pointer-events-none"
          style={{
            background:
              "linear-gradient(180deg, transparent 0%, rgba(255,211,106,0.55) 50%, transparent 100%)",
          }}
        />

        {/* Header */}
        <div className="px-4 pt-4 pb-3 text-center relative">
          <div
            className="inline-flex items-center gap-2 text-base font-bold tracking-[0.18em]"
            style={{
              color: "#FFD36A",
              textShadow: "0 0 12px rgba(255,211,106,0.40)",
            }}
          >
            <Trophy size={16} /> LEVEL INCOME
          </div>
          <div
            className="mt-1 mx-auto h-px"
            style={{
              maxWidth: "240px",
              background:
                "linear-gradient(90deg, transparent 0%, rgba(255,211,106,0.6) 50%, transparent 100%)",
            }}
          />
        </div>

        {/* Live status summary */}
        <div className="mx-3 mb-3 grid grid-cols-3 gap-2 text-center">
          <StatPill label="Self Deposit" value={`$${selfDep}`} highlight={selfDep >= 25} />
          <StatPill label="Directs ≥$50" value={d50} highlight={d50 >= 2} />
          <StatPill label="Directs ≥$100" value={d100} highlight={d100 >= 1} />
        </div>

        {/* Table header */}
        <div
          className="grid grid-cols-[40px_60px_1fr_70px] gap-2 px-3 py-2 text-[10px] uppercase tracking-[0.10em] font-semibold"
          style={{
            background:
              "linear-gradient(180deg, rgba(255,211,106,0.18) 0%, rgba(255,211,106,0.06) 100%)",
            color: "#FFE5A1",
            borderTop: "1px solid rgba(255,211,106,0.30)",
            borderBottom: "1px solid rgba(255,211,106,0.30)",
          }}
        >
          <div>Lvl</div>
          <div>Reward</div>
          <div>Requirement</div>
          <div className="text-right">Status</div>
        </div>

        {/* Rows */}
        <div className="divide-y divide-white/[0.04]" data-testid="level-income-rows">
          {visibleLevels.map((lvl) => (
            <LevelRow key={lvl.level} lvl={lvl} />
          ))}
        </div>

        {/* Expand / collapse */}
        {levels.length > 5 && (
          <button
            onClick={() => setExpanded((v) => !v)}
            className="w-full flex items-center justify-center gap-2 py-3 text-xs font-semibold transition-colors hover:bg-white/[0.03]"
            style={{
              color: "#FFD36A",
              borderTop: "1px solid rgba(255,211,106,0.20)",
            }}
            data-testid="level-income-toggle"
          >
            {expanded ? (
              <>
                <ChevronUp size={14} /> Show Less
              </>
            ) : (
              <>
                <ChevronDown size={14} /> Show All {levels.length} Levels
              </>
            )}
          </button>
        )}
      </div>
    </div>
  );
}

function StatPill({ label, value, highlight }) {
  return (
    <div
      className="rounded-lg px-2 py-2 border"
      style={{
        background: highlight
          ? "linear-gradient(180deg, rgba(34,197,94,0.10) 0%, rgba(34,197,94,0.03) 100%)"
          : "linear-gradient(180deg, rgba(255,255,255,0.04) 0%, rgba(255,255,255,0.01) 100%)",
        borderColor: highlight ? "rgba(34,197,94,0.40)" : "rgba(255,255,255,0.08)",
      }}
    >
      <div className="text-[9px] uppercase tracking-[0.10em] text-[var(--text-3)]">{label}</div>
      <div
        className="mono text-sm font-semibold mt-0.5"
        style={{ color: highlight ? "#86EFAC" : "var(--text-1)" }}
      >
        {value}
      </div>
    </div>
  );
}

function LevelRow({ lvl }) {
  const { level, reward_pct, unlocked, directs_50_need, directs_100_need, self_need, missing } = lvl;

  // Build requirement label
  let reqParts = [`≥$${self_need}`];
  if (directs_100_need > 0) {
    reqParts.push(`+${directs_100_need}d ($100)`);
  }
  if (directs_50_need > 0 && directs_50_need > directs_100_need) {
    // Show net $50 directs requirement (effective ≥$50 count)
    reqParts.push(`+${directs_50_need}d ($50)`);
  }
  const reqLabel = reqParts.join(" · ");

  // For locked levels: show what's missing as tooltip-style sub-text
  const missingLabel = !unlocked && missing.length > 0
    ? missing.map((m) => m.label).join(" + ")
    : null;

  return (
    <div
      className="grid grid-cols-[40px_60px_1fr_70px] gap-2 px-3 py-2.5 items-center text-xs"
      data-testid={`level-row-${level}`}
    >
      {/* Level number */}
      <div
        className="mono font-semibold"
        style={{ color: unlocked ? "#FFD36A" : "var(--text-3)" }}
      >
        {String(level).padStart(2, "0")}
      </div>

      {/* Reward % */}
      <div
        className="mono font-semibold"
        style={{ color: unlocked ? "#86EFAC" : "var(--text-3)" }}
      >
        {reward_pct}%
      </div>

      {/* Requirement */}
      <div className="min-w-0">
        <div
          className="text-[11px] truncate"
          style={{ color: "var(--text-2)" }}
          title={reqLabel}
        >
          {reqLabel}
        </div>
        {missingLabel && (
          <div
            className="text-[10px] truncate mt-0.5"
            style={{ color: "#FCA5A5" }}
            title={missingLabel}
          >
            Need: {missingLabel}
          </div>
        )}
      </div>

      {/* Status */}
      <div className="flex justify-end">
        {unlocked ? (
          <div
            className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-semibold"
            style={{
              background: "rgba(34,197,94,0.16)",
              color: "#86EFAC",
              border: "1px solid rgba(34,197,94,0.35)",
            }}
            data-testid={`level-${level}-status`}
          >
            <CheckCircle2 size={10} /> Active
          </div>
        ) : (
          <div
            className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-semibold"
            style={{
              background: "rgba(255,255,255,0.04)",
              color: "var(--text-3)",
              border: "1px solid rgba(255,255,255,0.10)",
            }}
            data-testid={`level-${level}-status`}
          >
            <Lock size={10} /> Locked
          </div>
        )}
      </div>
    </div>
  );
}
